import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import { HelpCircle, Search, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicFAQPage() {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([axios.get(`${API}/public/faqs`), axios.get(`${API}/public/faq-categories`)])
      .then(([f, c]) => { setFaqs(f.data || []); setCategories(c.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const q = query.trim().toLowerCase();
  const visible = q
    ? faqs.filter((f) => f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q))
    : faqs;

  const sections = [
    ...categories.map((c) => ({ id: c.id, name: c.name, items: visible.filter((f) => f.category_id === c.id) })),
    { id: 'general', name: 'General', items: visible.filter((f) => !f.category_id || !categories.some((c) => c.id === f.category_id)) },
  ].filter((s) => s.items.length > 0);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12 space-y-6">
        <button
          onClick={() => navigate('/login')}
          data-testid="faq-back-to-login"
          className="flex items-center gap-2 text-sm text-zinc-400 hover:text-yellow-400 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to login
        </button>

        <div className="border-b border-zinc-800 pb-5">
          <h1 className="text-3xl sm:text-4xl font-barlow tracking-widest text-yellow-400 flex items-center gap-3">
            <HelpCircle className="w-8 h-8" strokeWidth={1.5} /> FAQ
          </h1>
          <p className="text-sm text-zinc-400 mt-2">S.A.N. Security Ltd — frequently asked questions.</p>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search questions…"
            data-testid="public-faq-search"
            className="pl-9 rounded-sm bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500"
          />
        </div>

        {loading ? (
          <p className="text-sm text-zinc-500">Loading…</p>
        ) : sections.length === 0 ? (
          <p className="text-sm text-zinc-500 py-8 text-center" data-testid="public-faq-empty">
            {q ? 'No questions match your search.' : 'No questions published yet. Check back soon.'}
          </p>
        ) : (
          <div className="space-y-8">
            {sections.map((section) => (
              <div key={section.id} className="space-y-2" data-testid={`public-faq-section-${section.id}`}>
                <h2 className="text-sm font-barlow tracking-widest uppercase text-yellow-400 border-l-2 border-yellow-400 pl-3">
                  {section.name}
                </h2>
                <Accordion type="single" collapsible className="space-y-2">
                  {section.items.map((f) => (
                    <AccordionItem key={f.id} value={f.id} className="border border-zinc-800 rounded-sm px-4 bg-zinc-900">
                      <AccordionTrigger className="text-sm font-medium text-left text-white hover:no-underline hover:text-yellow-400" data-testid={`public-faq-q-${f.id}`}>
                        {f.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-zinc-400 whitespace-pre-wrap">
                        {f.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
